/**
 * ReplayViewer — top-level cinematic replay page.
 *
 * Fetches a mission log, creates a ReplayEngine + CameraDirector,
 * and renders a Viewport3D with cinematic overlays.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { Viewport3D } from "../viewport/Viewport3D";
import { ReplayEngine } from "./ReplayEngine";
import { CameraDirector } from "./CameraDirector";
import { CinematicOverlay } from "./CinematicOverlay";
import { DialogueOverlay } from "./DialogueOverlay";
import { ReplayHUD } from "./ReplayHUD";
import type { MissionLog, DialogueEvent } from "../../systems/MissionLogger";
import type { SpatialState, AlertLevel } from "../../types/game";
import type { ActiveEffect } from "../../systems/SceneCommandExecutor";
import type { ReplayFrame } from "./ReplayEngine";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

interface Props {
  sessionId: string;
  onExit: () => void;
}

export function ReplayViewer({ sessionId, onExit }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Engine + director refs (not state — updated per-frame)
  const engineRef = useRef<ReplayEngine | null>(null);
  const directorRef = useRef<CameraDirector | null>(null);

  // State pushed to child components each frame
  const [spatialState, setSpatialState] = useState<SpatialState | null>(null);
  const [activeEffects, setActiveEffects] = useState<ActiveEffect[]>([]);
  const [alertLevel, setAlertLevel] = useState<AlertLevel>("normal");
  const [dialogueQueue, setDialogueQueue] = useState<DialogueEvent[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [missionName, setMissionName] = useState("");
  const [missionEnded, setMissionEnded] = useState(false);
  const [endSummary, setEndSummary] = useState<string | null>(null);

  // Chapter markers for the timeline
  const [chapters, setChapters] = useState<{ t: number; type: string }[]>([]);

  // Track cinematic fade state
  const [fadeState, setFadeState] = useState<"in" | "out" | "none">("none");
  const prevAlertRef = useRef<AlertLevel>("normal");

  // Animation frame ref
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef(0);

  // ── Fetch mission log and initialize engine ──
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/mission-log/${sessionId}`);
        if (!res.ok) throw new Error(`Failed to load mission log: ${res.status}`);
        const log: MissionLog = await res.json();

        if (cancelled) return;

        const engine = new ReplayEngine(log);
        const director = new CameraDirector(log.initialEncounter.spatialState);

        engineRef.current = engine;
        directorRef.current = director;

        // Extract chapter markers
        const markers: { t: number; type: string }[] = [];
        for (const e of log.events) {
          if (e.type === "alert") markers.push({ t: e.t, type: "alert" });
          if (e.type === "effect") markers.push({ t: e.t, type: "combat" });
          if (e.type === "dialogue") markers.push({ t: e.t, type: "dialogue" });
          if (e.type === "mission_end") markers.push({ t: e.t, type: "end" });
        }
        setChapters(markers);

        // Mission name from encounter state
        const enc = log.initialEncounter;
        setMissionName(enc.scenario ?? enc.location ?? "Mission Replay");
        setDuration(engine.duration);
        setAlertLevel(enc.alertLevel || "normal");
        prevAlertRef.current = enc.alertLevel || "normal";

        setLoading(false);

        // Auto-play after a brief pause for the title card
        setTimeout(() => {
          if (!cancelled && engineRef.current) {
            engineRef.current.play();
            setIsPlaying(true);
          }
        }, 3000);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ── Animation loop ──
  useEffect(() => {
    if (loading || error) return;

    function animate(timestamp: number) {
      const engine = engineRef.current;
      const director = directorRef.current;
      if (!engine || !director) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      const delta = lastTimeRef.current ? timestamp - lastTimeRef.current : 16;
      lastTimeRef.current = timestamp;

      const frame = engine.tick(delta);

      // Push new dialogue events into the queue
      if (frame.newDialogue.length > 0) {
        setDialogueQueue(prev => [...prev, ...frame.newDialogue]);
      }

      // Detect alert level transitions for cinematic fades
      if (frame.alertLevel !== prevAlertRef.current) {
        setFadeState("in");
        setTimeout(() => setFadeState("out"), 500);
        setTimeout(() => setFadeState("none"), 1500);
        prevAlertRef.current = frame.alertLevel;
      }

      // Check for mission end (explicit event or playback finished)
      if (frame.missionEnded) {
        setMissionEnded(true);
        setEndSummary(frame.missionEnded.summary);
      } else if (!engine.isPlaying && engine.currentTime >= engine.duration && engine.duration > 0) {
        setMissionEnded(true);
        setEndSummary(null);
      }

      // Update state for rendering (batch these)
      setSpatialState(frame.spatial);
      setActiveEffects(frame.activeEffects);
      setAlertLevel(frame.alertLevel);
      setCurrentTime(engine.currentTime);
      setIsPlaying(engine.isPlaying);

      rafRef.current = requestAnimationFrame(animate);
    }

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [loading, error]);

  // ── Camera override function passed to Viewport3D ──
  const cameraOverride = useCallback(
    (camera: THREE.PerspectiveCamera, delta: number) => {
      const director = directorRef.current;
      const engine = engineRef.current;
      if (!director || !engine) return;

      const frame: ReplayFrame = {
        spatial: spatialState ?? engine.missionLog.initialEncounter.spatialState,
        activeEffects,
        newDialogue: [],
        newEffects: [],
        newSceneCommands: [],
        newIntents: [],
        newStateChanges: [],
        newPlayerActions: [],
        alertLevel,
        missionEnded: null,
      };

      const shot = director.update(frame, engine.currentTime, delta * 1000);

      // Smoothly lerp camera toward target shot
      const lerpSpeed = shot.transitionSpeed * delta;
      camera.position.lerp(
        new THREE.Vector3(shot.position.x, shot.position.y, shot.position.z),
        Math.min(1, lerpSpeed)
      );

      const lookTarget = new THREE.Vector3(shot.lookAt.x, shot.lookAt.y, shot.lookAt.z);
      // Use a temp object to slerp look direction
      const currentDir = new THREE.Vector3();
      camera.getWorldDirection(currentDir);
      const targetDir = lookTarget.clone().sub(camera.position).normalize();
      currentDir.lerp(targetDir, Math.min(1, lerpSpeed));
      const newTarget = camera.position.clone().add(currentDir);
      camera.lookAt(newTarget);

      // FOV transition
      camera.fov += (shot.fov - camera.fov) * Math.min(1, lerpSpeed * 0.5);
      camera.updateProjectionMatrix();
    },
    [spatialState, activeEffects, alertLevel]
  );

  // ── Playback controls ──
  const handlePlayPause = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.toggle();
    setIsPlaying(engine.isPlaying);
  }, []);

  const handleSeek = useCallback((timeMs: number) => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.seek(timeMs);
    setCurrentTime(engine.currentTime);
    setMissionEnded(false);
    setEndSummary(null);
  }, []);

  const handleSpeedChange = useCallback((newSpeed: number) => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setSpeed(newSpeed);
    setSpeed(engine.speed);
  }, []);

  const handleDismissDialogue = useCallback(() => {
    setDialogueQueue(prev => prev.slice(1));
  }, []);

  // ── Loading / error states ──
  if (loading) {
    return (
      <div className="replay-loading">
        <div className="replay-loading-content">
          <div className="replay-loading-title">LOADING MISSION LOG</div>
          <div className="replay-loading-sub">{sessionId}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="replay-loading">
        <div className="replay-loading-content">
          <div className="replay-loading-title" style={{ color: "var(--color-red, #ff4444)" }}>
            REPLAY ERROR
          </div>
          <div className="replay-loading-sub">{error}</div>
          <button className="replay-exit-btn" onClick={onExit}>RETURN</button>
        </div>
      </div>
    );
  }

  return (
    <div className="replay-container">
      {/* 3D viewport — full screen, driven by replay engine */}
      <Viewport3D
        alertLevel={alertLevel}
        executor={null}
        replaySpatial={spatialState}
        replayEffects={activeEffects}
        cameraOverride={cameraOverride}
      />

      {/* Cinematic overlays */}
      <CinematicOverlay
        missionName={missionName}
        showTitleCard={currentTime < 4000}
        fadeState={fadeState}
        missionEnded={missionEnded}
        endSummary={endSummary}
      />

      {/* Dialogue display */}
      <DialogueOverlay
        dialogueQueue={dialogueQueue}
        onDismiss={handleDismissDialogue}
        initialEncounter={engineRef.current?.missionLog.initialEncounter}
      />

      {/* Playback controls */}
      <ReplayHUD
        isPlaying={isPlaying}
        currentTime={currentTime}
        duration={duration}
        speed={speed}
        chapters={chapters}
        onPlayPause={handlePlayPause}
        onSeek={handleSeek}
        onSpeedChange={handleSpeedChange}
        onExit={onExit}
      />
    </div>
  );
}
